import { useEffect, useState } from "react";
import { MapPin, Factory, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { FadeIn, SectionTag, ParallaxImage } from "@/components/Reveal";
import IndiaMap from "@/components/IndiaMap";
import SEO from "@/components/SEO";

const CONTAINERS_IMG =
  "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?q=80&w=1600&auto=format&fit=crop";

const PLANT_CAPACITY = [
  { label: "Bitumen", value: "200 MT/day" },
  { label: "Polymer Modified Bitumen", value: "150 MT/day" },
  { label: "Crumb Rubber Modified", value: "150 MT/day" },
  { label: "Bitumen Emulsion", value: "150 MT/day" },
];

export default function Infrastructure() {
  const [locations, setLocations] = useState(null);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([]));
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  if (!locations) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="infrastructure-loading" />;

  const hq = locations.filter((l) => l.type === "hq");
  const plants = locations.filter((l) => l.type === "plant");
  const presence = locations.filter((l) => l.type === "presence");

  return (
    <main data-testid="infrastructure-page">
      <SEO
        title="Infrastructure & Logistics — Catalyst Petrochem LLP"
        description="Two processing plants, port-side storage and a supply presence across 14+ Indian locations delivering certified bitumen on schedule, pan-India."
        path="/infrastructure"
      />
      <PageHeader
        index="05"
        tag="Infrastructure & Logistics"
        title="A network built for bulk."
        description="Two processing plants, port-side storage and a presence across 14+ locations — so certified bitumen reaches your hot-mix plant on schedule, anywhere in India."
      />

      <section className="grid gap-px border-b border-white/10 bg-white/10 md:grid-cols-2">
        <div className="bg-[#0A0A0A] p-8 md:p-14">
          <SectionTag index="01" label="Plant Capacity" />
          <div className="mt-10 space-y-0" data-testid="capacity-list">
            {PLANT_CAPACITY.map((c, i) => (
              <FadeIn key={c.label} delay={i * 0.06} className="flex items-baseline justify-between border-t border-white/10 py-5 last:border-b">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400">{c.label}</span>
                <span className="font-mono text-xl text-brand md:text-2xl">{c.value}</span>
              </FadeIn>
            ))}
          </div>
        </div>
        <ParallaxImage src={CONTAINERS_IMG} alt="Container terminal aerial view" className="h-80 md:h-auto" />
      </section>

      <section className="border-b border-white/10 px-6 py-20 md:px-12 md:py-28 lg:px-20" data-testid="india-map-section">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <FadeIn className="lg:col-span-7">
            <IndiaMap locations={locations} />
            <p className="mt-3 text-right font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
              Map base: MapSVG, CC BY 4.0
            </p>
          </FadeIn>
          <div className="lg:col-span-5">
            <SectionTag index="02" label="The Network" />
            <h2 className="mt-6 font-display text-4xl font-semibold uppercase leading-[1.08] tracking-normal sm:text-5xl sm:leading-[1.08]">
              One map. Every corridor.
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-neutral-400">
              From Srinagar to Mangalore, Durgapur to Surat — our supply network covers India&apos;s busiest
              infrastructure corridors with port-side imports, plant processing and last-mile delivery.
            </p>
            <div className="mt-10 space-y-0" data-testid="map-legend">
              {[
                { color: "#FF5722", shape: "diamond", label: "Corporate HQ", value: "New Delhi" },
                { color: "#FF5722", shape: "dot", label: "Processing Plants", value: "Jhajjar · Kosi Kalan" },
                { color: "#d4d4d4", shape: "dot", label: "Supply Presence", value: `${presence.length} cities` },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-4 border-t border-white/10 py-5 last:border-b">
                  {l.shape === "diamond" ? (
                    <span className="h-3 w-3 rotate-45" style={{ background: l.color, boxShadow: "0 0 10px rgba(255,87,34,0.8)" }} />
                  ) : (
                    <span className="h-3 w-3 rounded-full" style={{ background: l.color }} />
                  )}
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">{l.label}</span>
                  <span className="ml-auto font-mono text-sm text-white">{l.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:px-12 md:py-28 lg:px-20">
        <SectionTag index="03" label="Footprint" />
        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3">
          <FadeIn className="bg-[#0A0A0A] p-10">
            <Building2 className="h-6 w-6 text-brand" />
            <h3 className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">Corporate HQ</h3>
            {hq.map((l) => (
              <div key={l.id} className="mt-5 border-t border-white/10 pt-5" data-testid="hq-card">
                <p className="font-display text-2xl font-semibold uppercase tracking-tight">{l.city}</p>
                <p className="mt-2 text-sm text-neutral-500">{l.detail}</p>
              </div>
            ))}
          </FadeIn>
          <FadeIn delay={0.08} className="bg-[#0A0A0A] p-10">
            <Factory className="h-6 w-6 text-brand" />
            <h3 className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">Processing Plants</h3>
            {plants.map((l) => (
              <div key={l.id} className="mt-5 border-t border-white/10 pt-5" data-testid={`plant-${l.id}`}>
                <p className="font-display text-2xl font-semibold uppercase tracking-tight">
                  {l.city}, <span className="text-neutral-500">{l.state}</span>
                </p>
                <p className="mt-2 text-sm text-neutral-500">{l.detail}</p>
              </div>
            ))}
          </FadeIn>
          <FadeIn delay={0.16} className="bg-[#0A0A0A] p-10">
            <MapPin className="h-6 w-6 text-brand" />
            <h3 className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">Pan-India Presence</h3>
            <div className="mt-5 grid grid-cols-2 gap-x-4" data-testid="presence-grid">
              {presence.map((l) => (
                <div key={l.id} className="border-t border-white/10 py-3">
                  <p className="text-sm text-white">{l.city}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-600">{l.state}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
      {gallery.length > 0 && (
        <section className="border-t border-white/10 px-6 py-20 md:px-12 md:py-28 lg:px-20" data-testid="gallery-section">
          <SectionTag index="04" label="On The Ground" />
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((g, i) => (
              <FadeIn key={g.id} delay={i * 0.05} className="group relative bg-[#0A0A0A]">
                <div className="clip-frame overflow-hidden">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${g.storage_path}`}
                    alt={g.caption || "Catalyst Petrochem operations"}
                    className="h-72 w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100"
                    data-testid={`gallery-image-${g.id}`}
                  />
                </div>
                <div className="flex items-center justify-between px-5 py-4">
                  <p className="text-sm text-neutral-300">{g.caption || g.original_filename}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{g.category}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
